import React, { useRef, useState } from 'react';

import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';
import ServiceForm from 'components/shared/NewServiceModal/components/ServiceForm';
import Breadcrumb, { BreadcrumbList } from 'components/shared/Breadcrumb/Breadcrumb';
import { useBreadcrumbContext } from 'components/shared/Breadcrumb/context/BreadcrumbContext';

import ServicesTable from './components/ServicesTable';
import PriceAdjustments from './components/PriceAdjustments';
import { useAddServicesModal } from './hooks/useAddServicesModal';
import { addServicesModalColumns, buildFooterActions } from './lib/helpers';

const CREATE_SERVICE_STEP = 'create-service';

const AddServicesModalContent = ({
  isOpen,
  onClose,
  practice,
  practiceId,
  onServicesAdded,
  isAdminContext
}) => {
  const { currentStep, nextCrumb, goBack } = useBreadcrumbContext();
  const [columnDefs, setColumnDefs] = useState(addServicesModalColumns);
  const [isCreateFormChanged, setIsCreateFormChanged] = useState(false);
  const serviceFormRef = useRef(null);

  const isCreateStep = currentStep === CREATE_SERVICE_STEP;

  const {
    selectedRows,
    standardChargeMultiplier,
    selfPayMultiplier,
    selectedSpecialty,
    gridApi,
    page,
    limit,
    filters,
    tabsData,
    paginatedAndFilteredData,
    totalItems,
    showControls,
    isLoading,
    isFetching,
    isError,
    setSelectedSpecialty,
    setGridApi,
    setPage,
    setLimit,
    setLocalEdits,
    changeFilters,
    handleStandardChargeChange,
    handleSelfPayChange,
    handleAddServices,
    handleCreateNewService,
    onSelectionChanged,
    removeCode,
    retry
  } = useAddServicesModal({ practice, practiceId, onServicesAdded, onClose });

  const handleColumnChange = (updatedColumns) => {
    setColumnDefs((prev) =>
      typeof updatedColumns === 'function' ? updatedColumns(prev) : updatedColumns
    );
  };

  const handleCreateNewServiceWrapper = async (newService) => {
    const success = await handleCreateNewService(newService);
    if (success) goBack();
  };

  const footerActions = buildFooterActions({
    isCreateStep,
    isCreateFormChanged,
    isLoading,
    selectedRows,
    onGoBack: goBack,
    onSubmitForm: () => serviceFormRef.current?.submitForm(),
    onAddServices: handleAddServices
  });

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      width="medium"
      customHeader={
        <div className="flex w-full items-center justify-between gap-2">
          <BreadcrumbList />
          {!isCreateStep && (
            <Button
              text="Create new service"
              variant="link"
              color="neutral"
              iconSize={16}
              size="xs"
              shade={700}
              className="whitespace-nowrap text-sm"
              icon="add-square-mono"
              iconColor="neutral"
              onClick={() => nextCrumb({ name: CREATE_SERVICE_STEP, title: 'Create new service' })}
            />
          )}
        </div>
      }
      footerActions={footerActions}>
      {isCreateStep ? (
        <div className="flex flex-1 justify-center">
          <ServiceForm
            ref={serviceFormRef}
            service={{}}
            isAdminContext={isAdminContext}
            practice={practice}
            onSave={handleCreateNewServiceWrapper}
            onDirtyChange={setIsCreateFormChanged}
          />
        </div>
      ) : (
        <div className="flex h-full flex-col justify-between">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-4 flex flex-shrink-0 flex-col gap-1">
              <Typography weight="semibold" className="text-neutral-900">
                Choose services to add
              </Typography>
              <Typography className="text-sm text-neutral-600">
                Browse all services or use predefined categories to quickly add the ones relevant to
                your practice.
              </Typography>
            </div>

            <ServicesTable
              columnDefs={columnDefs}
              tabsData={tabsData}
              selectedSpecialty={selectedSpecialty}
              onSpecialtyChange={setSelectedSpecialty}
              filters={filters}
              onFilterChange={changeFilters}
              showControls={showControls}
              onColumnChange={handleColumnChange}
              onColumnDragEnd={handleColumnChange}
              paginatedAndFilteredData={paginatedAndFilteredData}
              gridApi={gridApi}
              onGridReady={({ api }) => setGridApi(api)}
              onSelectionChanged={onSelectionChanged}
              page={page}
              limit={limit}
              totalItems={totalItems}
              onPageChange={setPage}
              onLimitChange={setLimit}
              setLocalEdits={setLocalEdits}
              onRemove={removeCode}
              isAdminContext={isAdminContext}
              practice={practice}
              isLoading={isFetching}
              isError={isError}
              onRetry={retry}
            />
          </div>

          <div className="my-6 h-px w-full bg-neutral-200" />

          <PriceAdjustments
            standardChargeMultiplier={standardChargeMultiplier}
            selfPayMultiplier={selfPayMultiplier}
            onStandardChargeChange={handleStandardChargeChange}
            onSelfPayChange={handleSelfPayChange}
          />
        </div>
      )}
    </Modal>
  );
};

const AddServicesModal = ({
  isOpen,
  onClose,
  practice,
  practiceId,
  onServicesAdded = () => {},
  isAdminContext = false
}) => (
  <Breadcrumb steps={[{ name: 'add-services', title: 'Add services' }]}>
    <AddServicesModalContent
      isOpen={isOpen}
      onClose={onClose}
      practice={practice}
      practiceId={practiceId}
      onServicesAdded={onServicesAdded}
      isAdminContext={isAdminContext}
    />
  </Breadcrumb>
);

export default AddServicesModal;
